import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User, Building2, Mail, Shield, Save, Clock, KeyRound } from "lucide-react";

const EMPTY_PW = { current: "", next: "", confirm: "" };

const PW_FIELDS = [
  { key: "current", label: "Current password", auto: "current-password" },
  { key: "next", label: "New password", auto: "new-password" },
  { key: "confirm", label: "Confirm new password", auto: "new-password" },
];

function ChangePasswordCard({ api }) {
  const [pw, setPw] = useState(EMPTY_PW);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setDone(false);
    if (!pw.current || !pw.next || !pw.confirm) return setError("Please fill in all three fields.");
    if (pw.next !== pw.confirm) return setError("New password and confirmation do not match.");
    if (pw.next === pw.current) return setError("New password must be different from the current password.");
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw(EMPTY_PW);
      setDone(true);
    } catch (err) {
      setError(extractErrorMessage(err, "Could not update password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-slate-400" />
          Change password
        </CardTitle>
        <CardDescription>Update the password you use to sign in</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          {PW_FIELDS.map((f) => (
            <div className="space-y-2" key={f.key}>
              <Label htmlFor={`pw-${f.key}`}>{f.label}</Label>
              <Input id={`pw-${f.key}`} type="password" autoComplete={f.auto} value={pw[f.key]}
                onChange={(e) => setPw({ ...pw, [f.key]: e.target.value })} data-testid={`pw-${f.key}-input`} />
            </div>
          ))}
          {error && <p className="text-sm text-red-600" data-testid="change-password-error">{error}</p>}
          {done && <p className="text-sm text-emerald-600" data-testid="change-password-success">Password updated.</p>}
          <Button type="submit" disabled={busy} className="bg-sky-700 hover:bg-sky-800" data-testid="update-password-btn">
            Update password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, refreshUser, api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [orgName, setOrgName] = useState(user?.org_name || "");
  const [exerciseFreq, setExerciseFreq] = useState("");
  const [freqRecommended, setFreqRecommended] = useState(null);
  const [freqSource, setFreqSource] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/organizations/current");
        const org = res.data || {};
        setExerciseFreq(org.exercise_frequency_months ? String(org.exercise_frequency_months) : "");
        setFreqRecommended(org.exercise_frequency_recommended ?? null);
        setFreqSource(org.exercise_frequency_source || "");
        if (org.name) setOrgName(org.name);
      } catch (e) { /* ignore */ }
    })();
  }, [api]);

  const handleSaveOrg = async () => {
    if (!orgName.trim()) {
      toast.error("Organization name is required");
      return;
    }

    setSaving(true);
    try {
      const trimmed = String(exerciseFreq).trim();
      const efm = trimmed === "" ? null : parseInt(trimmed, 10);
      if (efm !== null && (isNaN(efm) || efm < 0)) {
        toast.error("Exercise frequency must be a positive number of months");
        setSaving(false);
        return;
      }
      await api.put("/organizations/current", { name: orgName, exercise_frequency_months: efm });
      toast.success("Organization updated!");
      await refreshUser();
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to update organization");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-600 mt-1">Manage your account and organization settings.</p>
      </div>

      {/* Profile Card */}
      <Card data-testid="profile-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-slate-400" />
            Profile Information
          </CardTitle>
          <CardDescription>Your personal account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-500">First Name</Label>
              <Input value={user?.first_name || ""} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">Last Name</Label>
              <Input value={user?.last_name || ""} disabled className="bg-slate-50" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-500">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={user?.email || ""} disabled className="bg-slate-50 pl-10" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-500">Role</Label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={user?.role?.toUpperCase() || "VIEWER"} disabled className="bg-slate-50 pl-10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <ChangePasswordCard api={api} />

      {/* Organization Card */}
      <Card data-testid="organization-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-400" />
            Organization Settings
          </CardTitle>
          <CardDescription>Manage your organization details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org_name">Organization Name</Label>
            <Input
              id="org_name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter organization name"
              data-testid="org-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-500">Organization ID</Label>
            <Input value={user?.org_id || ""} disabled className="bg-slate-50 font-mono text-sm" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="exercise_freq" className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" /> Exercise Frequency (months)
            </Label>
            <Input
              id="exercise_freq"
              type="number"
              min="0"
              value={exerciseFreq}
              onChange={(e) => setExerciseFreq(e.target.value)}
              placeholder="Leave blank to use recommended"
              data-testid="exercise-frequency-input"
            />
            {freqRecommended != null && (
              <p className="text-xs text-slate-500" data-testid="exercise-frequency-recommendation">
                Recommended: {freqRecommended} months{freqSource ? ` based on ${freqSource}` : ""}. Leave blank to use this.
              </p>
            )}
          </div>
          <Button
            onClick={handleSaveOrg}
            disabled={saving}
            className="bg-sky-700 hover:bg-sky-800"
            data-testid="save-org-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card data-testid="account-info-card">
        <CardHeader>
          <CardTitle className="text-lg">Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500">Account Created</p>
              <p className="font-medium text-slate-900">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Subscription Status</p>
              <p className={`font-medium ${user?.subscription_status === "active" ? "text-emerald-600" : "text-amber-600"}`}>
                {user?.subscription_status?.toUpperCase() || "PENDING"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="text-lg text-red-600">Danger Zone</CardTitle>
          <CardDescription>Irreversible and destructive actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
            <div>
              <p className="font-medium text-red-900">Delete Account</p>
              <p className="text-sm text-red-600">Permanently delete your account and all associated data.</p>
            </div>
            <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-100" disabled>
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
